/*
 * Banner images go to the API as base64 inside a JSON body, and the API rejects bodies over
 * 200 KB (EXPRESS_LIMIT), which ruled out most real banner files. This resizes the picked image
 * to at most 1600 px wide and re-encodes it as JPEG, lowering the quality (and then the size)
 * until the base64 fits comfortably under that limit.
 *
 * The app shows banners in a 16:6 box stretched to fit (BoxFit.fill), so 1600 x 600 is the
 * shape to design for.
 */
export const BANNER_ASPECT = 16 / 6;
export const BANNER_HINT = 'Recommended 1600 x 600 px (16:6, the app\'s banner shape). Larger images are resized automatically.';

const MAX_WIDTH = 1600;
const MAX_BASE64_CHARS = 180 * 1024;   // leaves room for the rest of the JSON under the 200 KB limit

const loadImage = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('This file is not an image the browser can read. Please choose a JPG, PNG or WEBP.')); };
    img.src = url;
});

/* Returns { image, extension, width, height } ready to send to Slider.save / Slider.update. */
export async function prepareBannerImage(file) {
    const img = await loadImage(file);
    let width = Math.min(MAX_WIDTH, img.naturalWidth);
    for (let attempt = 0; attempt < 6; attempt++) {
        const height = Math.max(1, Math.round(img.naturalHeight * width / img.naturalWidth));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';            // JPEG has no transparency
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        for (const quality of [0.9, 0.82, 0.74, 0.66, 0.58]) {
            const dataUrl = canvas.toDataURL('image/jpeg', quality);
            const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
            if (base64.length <= MAX_BASE64_CHARS) {
                return { image: base64, extension: 'jpeg', width, height };
            }
        }
        width = Math.round(width * 0.8);      // still too large: shrink and try again
    }
    throw new Error('Could not make this image small enough to upload. Please use a smaller or simpler image.');
}

/* Warn when a picture is far from 16:6, because the app will stretch it. */
export function aspectWarning(width, height) {
    if (!width || !height) return '';
    const ratio = width / height;
    if (Math.abs(ratio - BANNER_ASPECT) / BANNER_ASPECT < 0.12) return '';
    return `This image is ${width} x ${height}. The app shows banners at 16:6, so it will look stretched.`;
}
